import React, { useEffect, useState, useCallback } from "react";
import LabTechnicianSidebar from "./Techniciansidebar";
import issueService from "../services/issueService";
import {
  FaExclamationTriangle,
  FaClock,
  FaSpinner,
  FaCheckCircle,
  FaTools
} from "react-icons/fa";
import "../styles/TechnicianIssuesPage.css";

const TechnicianIssues = () => {

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [proofFiles, setProofFiles] = useState([]);

  // Fetch issues
  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
    
      
      // Use getAllIssues - it automatically filters by technician when called
      const response = await issueService.getAllIssues(
        statusFilter ? { status: statusFilter } : {}
      );

      
      setIssues(response.data || []);
    } catch (error) {
      console.error("Error fetching issues", error);
      alert("Failed to load issues. Please make sure you're logged in.");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  const getSeverityClass = (severity) => {
    return `severity-badge severity-${severity}`;
  };

  const getChecklistProgress = () => {
    if (!selectedIssue || !selectedIssue.checklist || selectedIssue.checklist.length === 0) {
      return 0;
    }
    const completed = selectedIssue.checklist.filter(item => item.completed).length;
    return Math.round((completed / selectedIssue.checklist.length) * 100);
  };

  const getStatusBadgeClass = (status) => {
    return `status-badge status-${status.replace('_', '-')}`;
  };

  const getSeverityBadgeClass = (severity) => {
    return `severity-badge severity-${severity}`;
  };

  const handleChecklistItemToggle = async (itemIndex) => {
    if (!selectedIssue) return;

    try {
      const updatedChecklist = [...selectedIssue.checklist];
      updatedChecklist[itemIndex] = {
        ...updatedChecklist[itemIndex],
        completed: !updatedChecklist[itemIndex].completed
      };

      const response = await issueService.updateIssueStatus(selectedIssue._id, {
        checklist: updatedChecklist
      });

      if (response.success) {
        setSelectedIssue(response.data);
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
      }
    } catch (error) {
      console.error('Error updating checklist:', error);
      alert('Error updating checklist: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !selectedIssue) return;

    try {
      setLoading(true);
      const response = await issueService.uploadAttachment(selectedIssue._id, file);
      if (response.success) {
        setSelectedIssue(response.data);
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
        alert('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      setLoading(true);
      const response = await issueService.deleteAttachment(selectedIssue._id, attachmentId);
      if (response.success) {
        setSelectedIssue(response.data);
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
        alert('File deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file');
    } finally {
      setLoading(false);
    }
  };

  const handleProofFileUpload = (event) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setProofFiles([...proofFiles, ...newFiles]);
    }
  };

  const handleRemoveProofFile = (index) => {
    setProofFiles(proofFiles.filter((_, i) => i !== index));
  };

  const handleStatusChange = async (issueId, newStatus) => {
    try {
      setLoading(true);
      const updateData = { status: newStatus };

      if (newStatus === 'resolved' && remarks) {
        updateData.technicianRemarks = remarks;
      }

      if (newStatus === 'rejected' && rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }

      // Handle proof file uploads for resolved status
      if (newStatus === 'resolved' && proofFiles.length > 0) {
        const formData = new FormData();
        formData.append('status', 'resolved');
        formData.append('technicianRemarks', remarks);
        formData.append('context', 'closure');
        
        // Add proof files
        proofFiles.forEach((file, index) => {
          formData.append(`proofFiles`, file);
        });

        // Upload files and update status
        const response = await issueService.updateIssueStatusWithProof(issueId, formData);
        if (response.success) {
          setIssues(issues.map(issue =>
            issue._id === issueId ? response.data : issue
          ));
          setSelectedIssue(null);
          setStatusAction(null);
          setRemarks('');
          setProofFiles([]);
          fetchIssues();
        }
      } else {
        const response = await issueService.updateIssueStatus(issueId, updateData);
        if (response.success) {
          setIssues(issues.map(issue =>
            issue._id === issueId ? response.data : issue
          ));
          setSelectedIssue(null);
          setStatusAction(null);
          setRemarks('');
          setProofFiles([]);
          setRejectionReason('');
          fetchIssues();
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating issue status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="technician-issues-page">
      <LabTechnicianSidebar user={null} />
      <div className="page-container">
        <div className="issues-page">

          <h2>
            <FaExclamationTriangle /> Technician Issues
          </h2>

          {/* FILTER */}

          <div className="filter-box">
            <label>Status</label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="reported">Reported</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {/* SUMMARY CARDS */}

          <div className="summary-row">
            <div className="summary-card reported">
              <FaClock />
              <div>
                <span>{issues.filter(i => i.status === "reported").length}</span>
                <p>Reported</p>
              </div>
            </div>

            <div className="summary-card progress">
              <FaTools />
              <div>
                <span>{issues.filter(i => i.status === "in_progress").length}</span>
                <p>In Progress</p>
              </div>
            </div>

            <div className="summary-card resolved">
              <FaCheckCircle />
              <div>
                <span>{issues.filter(i => i.status === "resolved").length}</span>
                <p>Resolved</p>
              </div>
            </div>

            <div className="summary-card total">
              <FaExclamationTriangle />
              <div>
                <span>{issues.length}</span>
                <p>Total</p>
              </div>
            </div>
          </div>

          {/* LOADING */}

          {loading && (
            <p className="loading">
              <FaSpinner className="spin" /> Loading issues...
            </p>
          )}

          {/* NO DATA */}

          {!loading && issues.length === 0 && (
            <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
              No issues assigned.
            </p>
          )}

          {/* TABLE */}

          {!loading && issues.length > 0 && (
            <table className="issues-table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Type</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Reporter</th>
                  <th>Reported Date</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {issues.map((issue) => (
                  <tr key={issue._id}>
                    <td>{issue.assetName || issue.assetId?.assetName || "—"}</td>
                    <td>
                      <span className="issue-type-badge">
                        {issue.issueType?.replace(/([A-Z])/g, ' $1').toUpperCase().trim() || "—"}
                      </span>
                    </td>
                    <td>
                      <span className={getSeverityClass(issue.severity)}>
                        {issue.severity?.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusClass(issue.status)}>
                        {issue.status.replace("_", " ").toUpperCase()}
                      </span>
                    </td>
                    <td>{issue.reportedBy?.name || "—"}</td>
                    <td>{formatDate(issue.createdAt)}</td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={() => setSelectedIssue(issue)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* MODAL */}

          {selectedIssue && (
        <div className="modal-overlay" onClick={() => setSelectedIssue(null)}>
          <div className="modal-content issue-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <span className="modal-icon">🔧</span>
                <div>
                  <h3>Issue Details</h3>
                  <span className="modal-subtitle">#{selectedIssue._id?.slice(-6).toUpperCase()}</span>
                </div>
              </div>
              <button
                className="close-btn"
                onClick={() => setSelectedIssue(null)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Status & Severity Badges Row */}
              <div className="modal-badges-row">
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Status</span>
                  <span className={`modal-badge ${getStatusBadgeClass(selectedIssue.status)}`}>
                    {selectedIssue.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Severity</span>
                  <span className={`modal-badge ${getSeverityBadgeClass(selectedIssue.severity)}`}>
                    {selectedIssue.severity.toUpperCase()}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Type</span>
                  <span className="modal-badge modal-badge-type">
                    {selectedIssue.issueType.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Asset Info Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Asset Information</h4>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Asset Name</span>
                    <span className="info-value">{selectedIssue.assetId?.assetName || '—'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value">{selectedIssue.assetId?.serialNumber || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Report Info Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Report Information</h4>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Reported By</span>
                    <span className="info-value">{selectedIssue.reportedBy?.name || 'Asset Incharge'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Reported Date</span>
                    <span className="info-value">{new Date(selectedIssue.reportedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Description</h4>
                <p className="modal-description">{selectedIssue.description}</p>
              </div>

              {selectedIssue.checklist && selectedIssue.checklist.length > 0 && (
                <div className="modal-info-card">
                  <h4 className="modal-card-title">Task Checklist <span className="checklist-pct">{getChecklistProgress()}%</span></h4>
                  <div className="checklist-progress">
                    <div className="progress-bar" style={{ width: `${getChecklistProgress()}%` }}></div>
                  </div>
                  <div className="checklist-interactive">
                    {selectedIssue.checklist.map((item, idx) => (
                      <div key={idx} className="checklist-item">
                        <input
                          type="checkbox"
                          id={`checklist-${idx}`}
                          checked={item.completed || false}
                          onChange={() => handleChecklistItemToggle(idx)}
                          disabled={selectedIssue.status === 'resolved' || selectedIssue.status === 'closed'}
                        />
                        <label htmlFor={`checklist-${idx}`} className={item.completed ? 'completed' : ''}>
                          {typeof item === 'string' ? item : item.text}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedIssue.technicianRemarks && (
                <div className="modal-info-card modal-remarks-card">
                  <h4 className="modal-card-title">Technician Remarks</h4>
                  <p className="modal-description">{selectedIssue.technicianRemarks}</p>
                </div>
              )}

              {selectedIssue.rejectionReason && (
                <div className="modal-info-card modal-rejection-card">
                  <h4 className="modal-card-title">Rejection Reason</h4>
                  <p className="modal-description">{selectedIssue.rejectionReason}</p>
                </div>
              )}

              {/* File Attachments Section */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Attachments</h4>
                <div className="attachments-section">
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      disabled={loading}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="btn btn-upload"
                      onClick={() => document.getElementById('file-input').click()}
                      disabled={loading}
                    >
                      + Upload File
                    </button>
                  </div>

                  {selectedIssue.attachments && selectedIssue.attachments.length > 0 && (
                    <div className="attachments-list">
                      <h4>Files ({selectedIssue.attachments.length}):</h4>
                      <ul>
                        {selectedIssue.attachments.map((attachment) => (
                          <li key={attachment._id} className="attachment-item">
                            <div className="attachment-info">
                              <span className="filename">{attachment.originalName}</span>
                              <span className="filesize">({(attachment.size / 1024).toFixed(2)} KB)</span>
                              <span className="upload-date">
                                Uploaded: {new Date(attachment.uploadedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <button
                              className="btn btn-delete-file"
                              onClick={() => handleDeleteAttachment(attachment._id)}
                              disabled={loading}
                              title="Delete file"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="modal-footer">
              {selectedIssue.status === 'pending' && (
                <>
                  <button
                    className="btn btn-accept"
                    onClick={() => handleStatusChange(selectedIssue._id, 'accepted')}
                    disabled={loading}
                  >
                    Accept
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setStatusAction('reject')}
                    disabled={loading}
                  >
                    Reject
                  </button>
                </>
              )}

              {selectedIssue.status === 'accepted' && (
                <button
                  className="btn btn-progress"
                  onClick={() => handleStatusChange(selectedIssue._id, 'in_progress')}
                  disabled={loading}
                >
                  Mark as In Progress
                </button>
              )}

              {selectedIssue.status === 'in_progress' && (
                <button
                  className="btn btn-resolve"
                  onClick={() => setStatusAction('resolve')}
                  disabled={loading}
                >
                  Mark as Resolved
                </button>
              )}

              <button
                className="btn btn-close"
                onClick={() => setSelectedIssue(null)}
              >
                Close
              </button>
            </div>

            {/* Rejection Form */}
            {statusAction === 'reject' && (
              <div className="action-form">
                <h4>Reject Issue</h4>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason"
                  rows="3"
                  required
                />
                <div className="form-buttons">
                  <button
                    className="btn btn-submit"
                    onClick={() =>
                      handleStatusChange(selectedIssue._id, 'rejected')
                    }
                    disabled={!rejectionReason || loading}
                  >
                    Submit Rejection
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => setStatusAction(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Resolution Form */}
            {statusAction === 'resolve' && (
              <div className="action-form">
                <h4>Mark as Resolved</h4>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter technician remarks"
                  rows="3"
                  required
                />
                
                {/* Proof File Upload Section */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Upload Proof (Images/Videos) - Optional
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleProofFileUpload}
                    style={{
                      display: 'block',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}
                  />
                  <small style={{ color: '#666' }}>
                    Accepted formats: Images (JPG, PNG, GIF) and Videos (MP4, WebM, MOV)
                  </small>
                </div>

                {/* Proof Files List */}
                {proofFiles.length > 0 && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#f0f4f8',
                    borderRadius: '4px'
                  }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                      Selected Proof Files: ({proofFiles.length})
                    </label>
                    {proofFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: '#fff',
                          marginBottom: '6px',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveProofFile(index)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-buttons">
                  <button
                    className="btn btn-submit"
                    onClick={() =>
                      handleStatusChange(selectedIssue._id, 'resolved')
                    }
                    disabled={!remarks || loading}
                  >
                    Submit Resolution
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => {
                      setStatusAction(null);
                      setProofFiles([]);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

        </div>
      </div>
    </div>
  );
};

export default TechnicianIssues;